import { useState, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useProjectContext } from '../contexts/ProjectContext';
import {
  useCreatePreventivo,
  useUpdatePreventivo,
  useDeletePreventivo,
  useCreateLavoro,
  useDeleteLavoro,
  useUpdateLavoro,
  useReorderLavori,
} from '../hooks';
import { PreventiviList } from '../components/Preventivi/PreventiviList';
import { PreventivoDetails } from '../components/Preventivi/PreventivoDetails';
import { ComputoMetricoWizard } from '../components/Preventivi/ComputoMetricoWizard';
import { LavoroCard } from '../components/Lavori/LavoroCard';
import { UploadQuoteWizard } from '../components/Lavori/UploadQuoteWizard';
import { ImportDocumentWizard } from '../components/Lavori/ImportDocumentWizard';
import { CmeView } from '../components/Lavori/CmeView';
import { EstimateLevelSelector } from '../components/Dashboard/EstimateLevelSelector';
import { formatCurrency, getPrice, isAttiva } from '../utils/pricing';
import type { Preventivo, EstimateLevel, Lavoro } from '../types';

type WizardState =
  | null
  | { type: 'ai'; lavoroId?: string; lavoroNome?: string; lavoroDescrizione?: string }
  | { type: 'upload'; lavoroId?: string; lavoroNome?: string }
  | { type: 'import-document' };

export function Preventivi() {
  const { currentProject } = useProjectContext();
  const [estimateLevel, setEstimateLevel] = useState<EstimateLevel>('med');

  const createMutation = useCreatePreventivo();
  const updateMutation = useUpdatePreventivo();
  const deleteMutation = useDeletePreventivo();
  const createLavoroMutation = useCreateLavoro();
  const deleteLavoroMutation = useDeleteLavoro();
  const updateLavoroMutation = useUpdateLavoro();
  const reorderLavoriMutation = useReorderLavori();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const isSaving = createMutation.isPending || updateMutation.isPending;

  const [selectedPreventivo, setSelectedPreventivo] = useState<Preventivo | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [wizardState, setWizardState] = useState<WizardState>(null);

  // CME view state — store only the ID, resolve from live data
  const [cmeViewLavoroId, setCmeViewLavoroId] = useState<string | null>(null);

  // Lavoro modal state
  const [lavoroModal, setLavoroModal] = useState<{ mode: 'create' | 'edit'; lavoro?: Lavoro } | null>(null);
  const [lavoroForm, setLavoroForm] = useState({ nome: '', descrizione: '', budgetTotale: '' });

  // Orphan preventivi (no lavoroId)
  const orphanPreventivi = useMemo((): Preventivo[] => {
    return (currentProject?.preventivi || []).filter((p: Preventivo) => !p.lavoroId);
  }, [currentProject?.preventivi]);

  // Total across all lavori + orphan.
  // Conta solo le voci attive di lavorazioni attive, come il CME e i documenti generati:
  // prima sommava tutto, quindi il totale in pagina era più alto di quello dei computi.
  const allPreventivi: Preventivo[] = currentProject?.preventivi || [];
  const lavoriSospesiIds = useMemo(
    () => new Set((currentProject?.lavori || []).filter((l: Lavoro) => l.attivo === false).map((l: Lavoro) => l.id)),
    [currentProject?.lavori]
  );
  const preventiviContati = useMemo(
    () => allPreventivi.filter((p) => isAttiva(p) && !(p.lavoroId && lavoriSospesiIds.has(p.lavoroId))),
    [allPreventivi, lavoriSospesiIds]
  );
  const grandTotal = useMemo(() => {
    return preventiviContati.reduce((sum: number, p: Preventivo) => sum + getPrice(p, estimateLevel), 0);
  }, [preventiviContati, estimateLevel]);
  const vociEscluse = allPreventivi.length - preventiviContati.length;

  const handleEdit = (preventivo: Preventivo) => {
    setSelectedPreventivo(preventivo);
  };

  const handleDelete = async (id: string) => {
    if (!currentProject) return;
    if (window.confirm('Sei sicuro di voler eliminare questo preventivo?')) {
      deleteMutation.mutate({ id, projectId: currentProject.id });
    }
  };

  const handleUpdate = async (data: Partial<Preventivo>) => {
    if (!selectedPreventivo || !currentProject) return;
    updateMutation.mutate(
      { id: selectedPreventivo.id, projectId: currentProject.id, data },
      { onSuccess: () => setSelectedPreventivo(null) }
    );
  };

  const handleAddNew = async (data: Omit<Preventivo, 'id' | 'projectId' | 'createdAt' | 'updatedAt'>) => {
    if (!currentProject) return;
    createMutation.mutate(
      { projectId: currentProject.id, ...data, note: data.note ?? undefined },
      { onSuccess: () => setIsAddingNew(false) }
    );
  };

  const handleCreateLavoro = () => {
    if (!currentProject || !lavoroForm.nome.trim()) return;
    createLavoroMutation.mutate(
      {
        projectId: currentProject.id,
        nome: lavoroForm.nome.trim(),
        descrizione: lavoroForm.descrizione.trim() || undefined,
        budgetTotale: lavoroForm.budgetTotale.trim() || undefined,
      },
      {
        onSuccess: () => {
          setLavoroModal(null);
          setLavoroForm({ nome: '', descrizione: '', budgetTotale: '' });
        },
      }
    );
  };

  const handleUpdateLavoro = () => {
    if (!currentProject || !lavoroModal?.lavoro || !lavoroForm.nome.trim()) return;
    updateLavoroMutation.mutate(
      {
        id: lavoroModal.lavoro.id,
        projectId: currentProject.id,
        data: {
          nome: lavoroForm.nome.trim(),
          descrizione: lavoroForm.descrizione.trim() || null,
          budgetTotale: lavoroForm.budgetTotale.trim() || null,
        },
      },
      {
        onSuccess: () => {
          setLavoroModal(null);
          setLavoroForm({ nome: '', descrizione: '', budgetTotale: '' });
        },
      }
    );
  };

  const handleDeleteLavoro = (lavoro: Lavoro) => {
    if (!currentProject) return;
    const msg = (lavoro.preventivi?.length || 0) > 0
      ? `Eliminare "${lavoro.nome}"? Le ${lavoro.preventivi!.length} voci di costo diventeranno "Altre voci".`
      : `Eliminare "${lavoro.nome}"?`;
    if (window.confirm(msg)) {
      deleteLavoroMutation.mutate({ id: lavoro.id, projectId: currentProject.id });
    }
  };

  const handleToggleAttivo = (lavoro: Lavoro) => {
    if (!currentProject) return;
    updateLavoroMutation.mutate({
      id: lavoro.id,
      projectId: currentProject.id,
      data: { attivo: lavoro.attivo === false },
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !currentProject) return;

    const lista: Lavoro[] = currentProject.lavori || [];
    const da = lista.findIndex((l) => l.id === active.id);
    const a = lista.findIndex((l) => l.id === over.id);
    if (da === -1 || a === -1) return;

    const ids = arrayMove(lista, da, a).map((l) => l.id);
    reorderLavoriMutation.mutate({ projectId: currentProject.id, ids });
  };

  const openEditLavoro = (lavoro: Lavoro) => {
    setLavoroForm({ nome: lavoro.nome, descrizione: lavoro.descrizione || '', budgetTotale: lavoro.budgetTotale || '' });
    setLavoroModal({ mode: 'edit', lavoro });
  };

  if (!currentProject) {
    return (
      <div className="text-center py-12">
        <h2 className="text-lg font-semibold text-gray-900">Nessun progetto selezionato</h2>
        <p className="mt-2 text-gray-600">Seleziona un progetto per gestire i lavori.</p>
      </div>
    );
  }

  const lavori: Lavoro[] = currentProject.lavori || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lavori e Preventivi</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gestisci lavori e preventivi per {currentProject.name}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <EstimateLevelSelector value={estimateLevel} onChange={setEstimateLevel} />
          <button
            onClick={() => setWizardState({ type: 'import-document' })}
            className="btn-secondary"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Importa CME
          </button>
          <button
            onClick={() => {
              setLavoroForm({ nome: '', descrizione: '', budgetTotale: '' });
              setLavoroModal({ mode: 'create' });
            }}
            className="btn-primary"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nuovo Lavoro
          </button>
        </div>
      </div>

      {/* Grand Total */}
      {allPreventivi.length > 0 && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 flex justify-between items-center">
          <div>
            <span className="text-sm text-primary-700 font-medium">
              Totale generale ({preventiviContati.length} voc{preventiviContati.length === 1 ? 'e' : 'i'})
            </span>
            {vociEscluse > 0 && (
              <p className="text-xs text-primary-600/70 mt-0.5">
                {vociEscluse} voc{vociEscluse === 1 ? 'e esclusa' : 'i escluse'} perché disattivate o in
                lavorazioni sospese
              </p>
            )}
          </div>
          <span className="text-lg font-bold text-primary-800 currency">
            {formatCurrency(grandTotal)}
          </span>
        </div>
      )}

      {/* Lavori list */}
      {lavori.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={lavori.map((l) => l.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {lavori.map((lavoro) => (
                <LavoroCard
                  key={lavoro.id}
                  lavoro={lavoro}
                  estimateLevel={estimateLevel}
                  projectId={currentProject.id}
                  onStimaAI={() =>
                    setWizardState({ type: 'ai', lavoroId: lavoro.id, lavoroNome: lavoro.nome, lavoroDescrizione: lavoro.descrizione || undefined })
                  }
                  onUploadQuote={() =>
                    setWizardState({ type: 'upload', lavoroId: lavoro.id, lavoroNome: lavoro.nome })
                  }
                  onViewCme={() => setCmeViewLavoroId(lavoro.id)}
                  onEdit={() => openEditLavoro(lavoro)}
                  onDelete={() => handleDeleteLavoro(lavoro)}
                  onToggleAttivo={() => handleToggleAttivo(lavoro)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Empty state */}
      {lavori.length === 0 && orphanPreventivi.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Nessun lavoro</h3>
          <p className="mt-2 text-sm text-gray-500">
            Crea un lavoro manualmente o importa un documento CME per estrarre automaticamente tutti i lavori.
          </p>
          <div className="flex items-center justify-center gap-3 mt-4">
            <button
              onClick={() => setWizardState({ type: 'import-document' })}
              className="btn-primary"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Importa Documento CME
            </button>
            <button
              onClick={() => {
                setLavoroForm({ nome: '', descrizione: '', budgetTotale: '' });
                setLavoroModal({ mode: 'create' });
              }}
              className="btn-secondary"
            >
              Crea lavoro manuale
            </button>
          </div>
        </div>
      )}

      {/* Orphan preventivi (Altre voci) */}
      {orphanPreventivi.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Altre voci
              <span className="ml-2 text-sm font-normal text-gray-500">
                (non assegnate a un lavoro)
              </span>
            </h2>
            <button onClick={() => setIsAddingNew(true)} className="btn-secondary text-xs py-1.5 px-3">
              <svg className="w-3.5 h-3.5 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Aggiungi voce
            </button>
          </div>
          <PreventiviList
            preventivi={orphanPreventivi}
            estimateLevel={estimateLevel}
            onEdit={handleEdit}
            onDelete={handleDelete}
            selectedId={selectedPreventivo?.id}
            onSelect={setSelectedPreventivo}
          />
        </div>
      )}

      {/* Edit Preventivo Modal */}
      {selectedPreventivo && (
        <PreventivoDetails
          preventivo={selectedPreventivo}
          onUpdate={handleUpdate}
          onClose={() => setSelectedPreventivo(null)}
          isLoading={isSaving}
        />
      )}

      {/* Add New Preventivo Modal */}
      {isAddingNew && (
        <NewPreventivoModal
          onSave={handleAddNew}
          onClose={() => setIsAddingNew(false)}
          isLoading={isSaving}
        />
      )}

      {/* CME Wizard (AI Stima) */}
      {wizardState?.type === 'ai' && (
        <ComputoMetricoWizard
          onClose={() => setWizardState(null)}
          lavoroId={wizardState.lavoroId}
          lavoroNome={wizardState.lavoroNome}
          lavoroDescrizione={wizardState.lavoroDescrizione}
        />
      )}

      {/* Upload Quote Wizard */}
      {wizardState?.type === 'upload' && (
        <UploadQuoteWizard
          onClose={() => setWizardState(null)}
          lavoroId={wizardState.lavoroId}
          lavoroNome={wizardState.lavoroNome}
        />
      )}

      {/* Import Document Wizard */}
      {wizardState?.type === 'import-document' && (
        <ImportDocumentWizard onClose={() => setWizardState(null)} />
      )}

      {/* CME View */}
      {cmeViewLavoroId && (() => {
        const lav = lavori.find((l) => l.id === cmeViewLavoroId);
        return lav ? (
          <CmeView
            lavoro={lav}
            estimateLevel={estimateLevel}
            projectId={currentProject.id}
            onClose={() => setCmeViewLavoroId(null)}
          />
        ) : null;
      })()}

      {/* Lavoro Create/Edit Modal */}
      {lavoroModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full animate-slide-up">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  {lavoroModal.mode === 'create' ? 'Nuovo Lavoro' : 'Modifica Lavoro'}
                </h2>
                <button
                  onClick={() => setLavoroModal(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div className="input-group">
                  <label htmlFor="lavoro-nome" className="input-label">Nome lavoro *</label>
                  <input
                    type="text"
                    id="lavoro-nome"
                    value={lavoroForm.nome}
                    onChange={(e) => setLavoroForm((f) => ({ ...f, nome: e.target.value }))}
                    className="w-full"
                    placeholder='es. "Rifare il bagno", "Cambiare 6 finestre"'
                    autoFocus
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="lavoro-desc" className="input-label">Descrizione (opzionale)</label>
                  <textarea
                    id="lavoro-desc"
                    value={lavoroForm.descrizione}
                    onChange={(e) => setLavoroForm((f) => ({ ...f, descrizione: e.target.value }))}
                    className="w-full"
                    rows={2}
                    placeholder="Dettagli aggiuntivi..."
                  />
                </div>
                <div className="input-group">
                  <label htmlFor="lavoro-budget" className="input-label">Budget (€, opzionale)</label>
                  <input
                    type="number"
                    id="lavoro-budget"
                    value={lavoroForm.budgetTotale}
                    onChange={(e) => setLavoroForm((f) => ({ ...f, budgetTotale: e.target.value }))}
                    className="w-full"
                    placeholder="es. 15000"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-6">
                <button
                  type="button"
                  onClick={() => setLavoroModal(null)}
                  className="btn-secondary flex-1"
                  disabled={createLavoroMutation.isPending || updateLavoroMutation.isPending}
                >
                  Annulla
                </button>
                <button
                  type="button"
                  onClick={lavoroModal.mode === 'create' ? handleCreateLavoro : handleUpdateLavoro}
                  className="btn-primary flex-1"
                  disabled={
                    !lavoroForm.nome.trim() ||
                    createLavoroMutation.isPending ||
                    updateLavoroMutation.isPending
                  }
                >
                  {(createLavoroMutation.isPending || updateLavoroMutation.isPending)
                    ? 'Salvataggio...'
                    : lavoroModal.mode === 'create'
                      ? 'Crea Lavoro'
                      : 'Salva'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// New Preventivo Modal Component
function NewPreventivoModal({
  onSave,
  onClose,
  isLoading,
}: {
  onSave: (data: Omit<Preventivo, 'id' | 'projectId' | 'createdAt' | 'updatedAt'>) => void;
  onClose: () => void;
  isLoading?: boolean;
}) {
  const [formData, setFormData] = useState({
    categoria: 'Infissi',
    voce: '',
    costoMin: '',
    costoMed: '',
    costoMax: '',
    note: '',
    ordine: 0,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      lavoroId: null,
      note: formData.note || null,
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const categorie = [
    'Infissi',
    'Impianti',
    'Finiture',
    'Muratura',
    'Esterni',
    'Arredamento',
    'Altro',
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-auto animate-slide-up">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Nuovo Preventivo
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="input-group">
              <label htmlFor="categoria" className="input-label">
                Categoria
              </label>
              <select
                id="categoria"
                name="categoria"
                value={formData.categoria}
                onChange={handleChange}
                className="w-full"
                required
              >
                {categorie.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label htmlFor="voce" className="input-label">
                Descrizione Voce
              </label>
              <input
                type="text"
                id="voce"
                name="voce"
                value={formData.voce}
                onChange={handleChange}
                className="w-full"
                placeholder="es. Finestre PVC doppio vetro"
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="input-group">
                <label htmlFor="costoMin" className="input-label">
                  Costo Min
                </label>
                <input
                  type="number"
                  id="costoMin"
                  name="costoMin"
                  value={formData.costoMin}
                  onChange={handleChange}
                  className="w-full"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="costoMed" className="input-label">
                  Costo Med
                </label>
                <input
                  type="number"
                  id="costoMed"
                  name="costoMed"
                  value={formData.costoMed}
                  onChange={handleChange}
                  className="w-full"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="input-group">
                <label htmlFor="costoMax" className="input-label">
                  Costo Max
                </label>
                <input
                  type="number"
                  id="costoMax"
                  name="costoMax"
                  value={formData.costoMax}
                  onChange={handleChange}
                  className="w-full"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            <div className="input-group">
              <label htmlFor="note" className="input-label">
                Note (opzionale)
              </label>
              <textarea
                id="note"
                name="note"
                value={formData.note}
                onChange={handleChange}
                className="w-full"
                rows={3}
                placeholder="Aggiungi eventuali note..."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary flex-1"
                disabled={isLoading}
              >
                Annulla
              </button>
              <button
                type="submit"
                className="btn-primary flex-1"
                disabled={isLoading}
              >
                {isLoading ? 'Salvataggio...' : 'Crea Preventivo'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
